/* ==========================================================================
   Gift with purchase: auto-add the free pajama pants
   - Config is printed per product by snippets/modal-upsell-product.liquid
     (window.giftWithPurchase). No config = original behavior.
   - One gift per garment unit. Size comes from the gift size picker shown on
     the product page (preselected with the garment's size, default M).
     Design comes from the product's `gift-design:` tag (default: negro).
   - Gift lines carry a hidden `_gift_for` property (garment variant id). The
     TOTAL quantity of gift lines linked to a garment always equals that
     garment's quantity, including changes made in the UpCart drawer.
   ========================================================================== */
(() => {
    const GIFT_PROP = '_gift_for'
    const DEFAULT_SIZE = 'M'
    const PICKER_ID = 'gift-size-select'

    const SIZE_ALIASES = {
        CH: ['CH', 'CHICA', 'CHICO', 'S', 'SMALL'],
        M: ['M', 'MED', 'MEDIANA', 'MEDIANO', 'MEDIUM'],
        G: ['G', 'GDE', 'GRANDE', 'L', 'LARGE'],
        EG: ['EG', 'XL', 'EXTRA GRANDE'],
        EEG: ['EEG', 'XXL', '2XL', 'EXTRA EXTRA GRANDE']
    }

    const stripAccents = (value) => String(value).normalize('NFD').replace(/[̀-ͯ]/g, '')

    const normalizeSize = (value) => {
        if (!value) return null
        const clean = stripAccents(value).toUpperCase()
            .replace(/\b(ADULTO|GRATIS)\b/g, '')
            .replace(/\s+/g, ' ')
            .trim()
        return Object.keys(SIZE_ALIASES).find((key) => SIZE_ALIASES[key].includes(clean)) || null
    }

    const handleize = (value) => stripAccents(value).toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

    const sizeFromOptions = (options = []) => {
        for (const option of options) {
            const size = normalizeSize(option)
            if (size) return size
        }
        return null
    }

    const hasDesign = (config, variant) => variant.options.some((option) => handleize(option).includes(config.design))

    // Priority: same size + design > same size (any design) > default size + design > anything available
    const pickGiftVariant = (config, garmentOptions) => {
        const available = config.giftVariants.filter((variant) => variant.available)
        const size = sizeFromOptions(garmentOptions) || DEFAULT_SIZE
        const hasSize = (variant, wanted) => sizeFromOptions(variant.options) === wanted

        return available.find((variant) => hasSize(variant, size) && hasDesign(config, variant))
            || available.find((variant) => hasSize(variant, size))
            || available.find((variant) => hasSize(variant, DEFAULT_SIZE) && hasDesign(config, variant))
            || available.find((variant) => hasSize(variant, DEFAULT_SIZE))
            || available[0]
            || null
    }

    // One option per size (first option of the gift variant), preferring the configured design
    const pickerChoices = (config) => {
        const bySize = new Map()
        config.giftVariants
            .filter((variant) => variant.available)
            .forEach((variant) => {
                const label = String(variant.options[0] || '').replace(/\bGRATIS\b/gi, '').trim()
                const current = bySize.get(label)
                if (!current || (!hasDesign(config, current) && hasDesign(config, variant))) {
                    bySize.set(label, variant)
                }
            })
        return Array.from(bySize, ([label, variant]) => ({ label, id: variant.id }))
    }

    const garmentById = (config, id) => config.garmentVariants.find((variant) => variant.id === Number(id))

    /* ---------- Gift size picker (product page) ---------- */
    const initGiftPicker = () => {
        const config = window.giftWithPurchase
        if (!config) return

        const $form = document.querySelector('#template-product form[data-product-id]')
        const $atcRow = $form?.querySelector('.btn-atc')?.parentElement
        if (!$form || !$atcRow) return

        const choices = pickerChoices(config)
        if (!choices.length) return

        const $picker = document.createElement('div')
        $picker.className = 'gift-picker d-flex align-items-center justify-content-between rounded-3 px-3 py-2 mb-4'
        $picker.style.cssText = 'border: 1px solid #D97700; background: #fff8ec;'
        $picker.innerHTML = `
            <div class="d-flex align-items-center me-3" style="min-width: 0">
                <img class="gift-picker-img rounded me-3 flex-shrink-0" width="48" height="48" alt="" loading="lazy" style="object-fit: cover">
                <div class="small lh-sm" style="min-width: 0">
                    <b>🎁 Pantalón de regalo</b>
                    <span class="gift-picker-title d-block text-muted text-truncate"></span>
                </div>
            </div>
            <div class="flex-shrink-0 text-end">
                <label class="small text-muted d-block mb-1" for="${PICKER_ID}">Talla</label>
                <select id="${PICKER_ID}" class="form-select form-select-sm w-auto"></select>
            </div>
        `
        $picker.querySelector('.gift-picker-img').src = config.image || ''
        $picker.querySelector('.gift-picker-title').textContent = config.title || ''

        const $select = $picker.querySelector('select')
        choices.forEach((choice) => {
            const $option = document.createElement('option')
            $option.value = choice.id
            $option.textContent = choice.label
            $select.appendChild($option)
        })

        // Preselect with the garment's size until the customer picks one manually
        let pickedManually = false
        const syncWithGarment = () => {
            if (pickedManually) return
            const garment = garmentById(config, $form.querySelector('[name="id"]')?.value)
            const suggested = garment && pickGiftVariant(config, garment.options)
            const match = suggested && choices.find((choice) => {
                const variant = config.giftVariants.find((v) => v.id === choice.id)
                return sizeFromOptions(variant.options) === sizeFromOptions(suggested.options)
                    && String(variant.options[0]) === String(suggested.options[0])
            })
            if (match) $select.value = match.id
        }

        $select.addEventListener('change', () => { pickedManually = true })
        $form.querySelectorAll('.product-option').forEach(($option) => {
            // product.js updates the hidden variant select on change; read it right after
            $option.addEventListener('change', () => setTimeout(syncWithGarment, 0))
        })

        $atcRow.parentElement.insertBefore($picker, $atcRow)
        syncWithGarment()
    }

    const chosenGiftVariant = (config, garmentOptions) => {
        const pickedId = Number(document.getElementById(PICKER_ID)?.value)
        const picked = pickedId && config.giftVariants.find((variant) => variant.id === pickedId && variant.available)
        return picked || pickGiftVariant(config, garmentOptions)
    }

    /* ---------- Cart requests ---------- */
    let isGiftRequest = false
    let isAddingToCart = false

    const giftFetch = async (url, body) => {
        isGiftRequest = true
        try {
            return await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            })
        } finally {
            isGiftRequest = false
        }
    }

    const addGiftFor = async (formData) => {
        const config = window.giftWithPurchase
        if (!config) return

        const garmentVariantId = Number(formData.get('id'))
        const garment = garmentById(config, garmentVariantId)
        if (!garment) return

        const gift = chosenGiftVariant(config, garment.options)
        if (!gift) return

        const quantity = Math.max(1, Number(formData.get('quantity')) || 1)

        await giftFetch('/cart/add.js', {
            items: [{
                id: gift.id,
                quantity: quantity,
                properties: { [GIFT_PROP]: String(garmentVariantId) }
            }]
        })
    }

    // Keeps the TOTAL quantity of gift lines linked to each garment equal to that
    // garment's quantity. A garment can have several gift lines (e.g. added twice
    // with different gift sizes): older lines keep their quantity, the newest one
    // absorbs the difference (cart.js lists the newest lines first).
    const syncGiftLines = async () => {
        const cart = await (await fetch('/cart.js')).json()
        const garmentQty = {}
        const giftLinesByGarment = {}

        cart.items.forEach((item) => {
            const linkedId = item.properties && item.properties[GIFT_PROP]
            if (linkedId) {
                (giftLinesByGarment[linkedId] = giftLinesByGarment[linkedId] || []).push(item)
            } else {
                garmentQty[item.variant_id] = (garmentQty[item.variant_id] || 0) + item.quantity
            }
        })

        const updates = {}
        Object.entries(giftLinesByGarment).forEach(([garmentId, lines]) => {
            let remaining = garmentQty[Number(garmentId)] || 0
            const oldestFirst = [...lines].reverse()

            oldestFirst.forEach((line, index) => {
                const isNewest = index === oldestFirst.length - 1
                const target = isNewest ? remaining : Math.min(line.quantity, remaining)
                remaining -= target
                if (target !== line.quantity) updates[line.key] = target
            })
        })

        if (!Object.keys(updates).length) return false

        await giftFetch('/cart/update.js', { updates: updates })
        return true
    }
    window.syncGiftLines = syncGiftLines

    const refreshUpcart = () => {
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart()
    }

    // Detect cart changes made by apps (UpCart drawer) and re-sync the gift lines.
    // Never sync while our own add-to-cart (garment + gift) is still in progress.
    const CART_CHANGE_URL = /\/cart\/(change|update)(\.js)?(\?|$)/
    let syncTimer = null
    const scheduleSync = () => {
        clearTimeout(syncTimer)
        syncTimer = setTimeout(async () => {
            if (isAddingToCart) {
                scheduleSync()
                return
            }
            try {
                if (await syncGiftLines()) refreshUpcart()
            } catch (error) {
                console.warn('[gift] Could not sync gift lines', error)
            }
        }, 400)
    }

    const originalFetch = window.fetch.bind(window)
    window.fetch = async (...args) => {
        const ownRequest = isGiftRequest
        const response = await originalFetch(...args)
        try {
            const url = typeof args[0] === 'string' ? args[0] : args[0]?.url
            if (!ownRequest && url && CART_CHANGE_URL.test(url)) scheduleSync()
        } catch (error) { /* never break the original request */ }
        return response
    }

    const originalXhrOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        try {
            if (url && CART_CHANGE_URL.test(String(url))) {
                this.addEventListener('load', scheduleSync)
            }
        } catch (error) { /* never break the original request */ }
        return originalXhrOpen.call(this, method, url, ...rest)
    }

    /* ---------- Hide the internal `_gift_for` property in app carts ---------- */
    const hideInternalProps = () => {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => (node.nodeValue.includes(GIFT_PROP) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT)
        })
        const nodes = []
        while (walker.nextNode()) nodes.push(walker.currentNode)

        nodes.forEach((node) => {
            let $el = node.parentElement
            if (!$el || ['SCRIPT', 'STYLE', 'TEXTAREA'].includes($el.tagName)) return
            // Climb to the whole "_gift_for: 123" row, never further
            while ($el.parentElement && $el.parentElement.textContent.trim().startsWith(GIFT_PROP)) {
                $el = $el.parentElement
            }
            if ($el.textContent.trim().startsWith(GIFT_PROP)) $el.style.display = 'none'
        })
    }

    let hideTimer = null
    new MutationObserver(() => {
        clearTimeout(hideTimer)
        hideTimer = setTimeout(hideInternalProps, 50)
    }).observe(document.body, { childList: true, subtree: true, characterData: true })
    hideInternalProps()

    /* ---------- Add to cart ---------- */
    // Same as the original handler in cart.js, plus the gift auto-add
    window.onSubmitAtcForm = async ($form, event) => {
        event.preventDefault()

        $form.classList.add('loading')
        isAddingToCart = true

        let product = {}
        try {
            const formData = new FormData($form)
            const response = await fetch('/cart/add.js', { method: 'POST', body: formData })
            product = await response.json()

            if (response.ok) {
                try {
                    await addGiftFor(formData)
                } catch (error) {
                    console.warn('[gift] Could not add gift', error)
                }
            }
        } finally {
            isAddingToCart = false
            scheduleSync()
        }

        $form.classList.remove('loading')

        if (document.querySelector('#modal-upsell-pdp')) {
            const modal = new bootstrap.Modal('#modal-upsell-pdp')
            modal.show()
        } else {
            window.loadOffcanvasCart()
        }

        const quantity = Number($form.querySelector('[name="quantity"]')?.value || 1)

        window.ttq?.track('AddToCart', {
            content_type: 'product',
            content_id: product.handle,
            content_name: product.title,
            quantity: quantity,
            price: product.price / 100,
            value: product.price / 100 * quantity,
            currency: 'USD'
        })
    }

    // Sync gift lines before every theme offcanvas cart refresh
    const originalLoadOffcanvasCart = window.loadOffcanvasCart
    if (typeof originalLoadOffcanvasCart === 'function') {
        window.loadOffcanvasCart = async (...args) => {
            if (!isAddingToCart) {
                try {
                    await syncGiftLines()
                } catch (error) {
                    console.warn('[gift] Could not sync gift lines', error)
                }
            }
            return originalLoadOffcanvasCart(...args)
        }
    }

    // Cart page: sync on load (quantity changes there reload the page)
    if (document.body.classList.contains('request-page-type-cart')) {
        syncGiftLines()
            .then((changed) => { if (changed) window.location.reload() })
            .catch((error) => console.warn('[gift] Could not sync gift lines', error))
    }

    // Sticky add-to-cart bar: use the same ajax handler so the gift is added too
    if (window.giftWithPurchase) {
        document.querySelectorAll('.product-sticky-atc form[action="/cart/add"]').forEach(($form) => {
            $form.addEventListener('submit', (event) => window.onSubmitAtcForm($form, event))
        })
    }

    initGiftPicker()
})()

// Put here your own Custom JS codes

$('.brands-btn').click(function(){
        $('.brand').removeClass('hidden');
        $('.brands-btn').addClass('hidden');
});